/* eslint-disable camelcase */

import { CURRENCY } from '@/domains/core/settings/constants';
import { computeDefaultTrackingData } from '@/domains/productDiscovery/algolia/components/AlgoliaProductCard/tracking';
import type { Detail } from '@/domains/productDiscovery/ProductPage/b2c/interfaces/Detail.model';
import type { ProductPrice } from '@/domains/productDiscovery/ProductPage/interfaces/Detail.common.model';
import { type Brand } from '@/domains/productDiscovery/ProductPage/modules/Brand/brand.model';
import type { Hit } from '@/domains/productDiscovery/Recommendation/interfaces/algolia';
import type { Product } from '@/domains/productDiscovery/Recommendation/interfaces/product';
import type { ProductCardRecommendationTracking } from '@/domains/productDiscovery/Recommendation/interfaces/tracking';
import type { TrackedProductDetails } from '@/domains/shoppingCart/common/interfaces/TrackedProductDetails';

import { generateBundlePrices } from '../../../services/algolia/utils/bundlePrice';
import {
  getDetailPrice,
  getPriceByPlatform,
  primaryOrSecondaryPrice,
} from './formatters';
import type {
  ViewBundleGtmEvent,
  ViewBundleProduct,
} from './mappers.interfaces';

export type RatingStats = {
  total: number;
  average: number;
};
/**
 * Map a product from Detail interface to Product interface
 * @param detail Product information from useDetail hook
 * @returns Product
 */
export const detailToProduct = (
  detail: Detail,
  brand: Brand,
  ratingStats: RatingStats,
): Product => ({
  sellerName: detail.seller?.name ?? '',
  brandName: brand.name ?? '',
  articleId: detail.articleId,
  banner: {
    categories: [],
  },
  bannerAlternate: {
    categories: [],
  },
  bestOffer: false,
  brandId: brand.id ?? 0,
  brandImageFullpath: brand.imageUrl ?? '',
  bundlePrices: generateBundlePrices({
    seller_country_id: (detail.seller?.country || 'FR') as string,
    prices: {
      per_item: {
        actual_price: {
          with_vat: {
            as_float: primaryOrSecondaryPrice(detail.price as ProductPrice)
              ?.amountWithVat,
          },
          without_vat: {
            as_float: primaryOrSecondaryPrice(detail.price as ProductPrice)
              ?.amountWithoutVat,
          },
        },
      },
    },
  } as Hit),
  hasBulk: false,
  categoriesLast: [],
  categoriesLastId: detail.categoryId ?? 0,
  categoriesL0Id: [detail.categoryId ?? 0],
  categoriesL1: [],
  categoriesL1Id: [detail.categoryId ?? 0],
  categoriesL2: [],
  categoriesL2Id: [detail.categoryId ?? 0],
  defaultTitle: detail.title ?? '',
  deliveryOffers: {
    minFee: {
      asFloat: 0,
      asString: '',
      decimal: '',
      integer: '',
      ranges: [],
    },
    minFeeTime: 0,
    minTime: 0,
    minTimeFee: {
      asFloat: 0,
      asString: '',
      decimal: '',
      integer: '',
      ranges: [],
    },
  },
  has1dayDelivery: false,
  has3xPayment: false,
  hasFreeDelivery: false,
  hasProConditions: true,
  hasPromoLabel: false,
  hasRelayDelivery: detail.delivery?.fulfillment ?? false,
  hasSample: false,
  imagePath: detail.media?.[0]?.regularUrl ?? '',
  imageFullpath: detail.media?.[0]?.largeUrl ?? '',
  indexable: detail.isIndexable,
  isMmf: detail.delivery?.fulfillment || false,
  isLocal: false,
  isSellerB2b: true,
  isSecondHand: detail.isSecondHand,
  market: 'b2b',
  modelId: detail.modelId,
  modelsCount: detail.models?.length ?? 0,
  price: getPriceByPlatform(detail.price?.primaryPrice) ?? 0,
  prices: getDetailPrice(detail),
  productId: `${detail.productId ?? 0}`,
  rating: ratingStats.average,
  ratingCount: ratingStats.total,
  sellerCountryId: detail.seller?.country ?? '',
  sellerId: detail.seller?.id ?? 0,
  thumbnails: [],
  title: detail.title ?? '',
  url: detail.articlePath ?? '',
});

/**
 * Map a product to match the A2C TrackedProductDetails Interface
 * @param product Product
 * @param position Index of the product in the cart
 * @returns Mapped product
 */
export const productToTrackedDetails = (
  product: Product,
  position: number,
  playlistLocation: string,
  reco_engine_name?: string,
): TrackedProductDetails => ({
  block_clicked: 'Bundle',
  event_trigger: playlistLocation,
  name: product.title,
  picture: product.imageFullpath,
  id: Number(product.productId),
  quantity: 1,
  stockSize: 1,
  sellerName: product.sellerName,
  brandName: product.brandName,
  isDeliveredByManomano: product.isMmf,
  b2b: product.hasProConditions ?? false,
  unitPrice: parseInt('', 10),
  reference: product.productId,
  oldUnitPrice: parseInt('', 10),
  prices: product.prices,
  pr_position: position,
  reco_engine_name,
});

/**
 * Create tracking info for each recommended product during the product bundle view event
 * @param product Product
 * @param index index of the product in the playlist array
 * @param engineName Recommendation engine name
 * @param playlistFingerprint Gtm fingerprint
 * @param playlistReconciliation Gtm reconciliation
 * @returns ViewBundleProduct
 */
const viewBundleProduct = (
  product: Product,
  index: number,
  engineName: string,
  playlistFingerprint: string | undefined,
  playlistReconciliation: string | undefined,
): ViewBundleProduct => ({
  list_name: 'Bundle',
  order_id: undefined, // not applicable in a playlist context
  order_ref: undefined, // not applicable in a playlist context
  pr_attributes_count: undefined, // not applicable since we do not have a product Detail
  pr_base_price: product.price,
  pr_brand_id: product.brandId,
  pr_brand_name: product.brandName,
  pr_category_id: product.categoriesL1Id?.[0],
  pr_category_name: product.categoriesL1?.[0],
  pr_degraded_detail: undefined, // not applicable since we do not have a product Detail
  pr_family_id: product.categoriesL0Id?.[0],
  pr_family_name: product.categoriesL0?.[0],
  pr_has_variant: undefined, // not applicable since we do not have a product Detail
  pr_id: product.articleId,
  pr_is_degraded: undefined, // not applicable since we do not have a product Detail
  pr_is_mf: product.isMmf,
  pr_is_pro: !!product.hasProConditions,
  pr_is_restocking: undefined, // not applicable in a playlist context
  pr_is_sponsored: undefined, // not applicable in a playlist context
  pr_is_top_sales: !!product.banner?.categories?.includes(
    `topSales.${product.categoriesLastId}`,
  ),
  pr_lowest_category_id: product.categoriesLastId,
  pr_lowest_category_name: product.categoriesLast?.[0],
  pr_md_count: undefined, // not applicable in a playlist context
  pr_md_top: undefined, // not applicable in a playlist context
  pr_model_id: product.modelId,
  pr_model_name: product.title,
  pr_name: product.defaultTitle,
  pr_position: index,
  pr_price: product.price,
  pr_product_id: product.productId,
  pr_quantity: undefined, // not applicable in a playlist context
  pr_rating: product.rating,
  pr_rating_count: product.ratingCount,
  pr_seller_id: product.sellerId,
  pr_seller_name: product.sellerName,
  pr_shipping_options: undefined, // not applicable since we do not have a product Detail
  pr_shipping_options_count: undefined, // not applicable since we do not have a product Detail
  pr_stock: undefined, // not applicable since we do not have a product Detail
  pr_subcategory_id: product.categoriesL2Id?.[0],
  pr_subcategory_name: product.categoriesL2?.[0],
  pr_subsubcategory_id: product.categoriesL3Id?.[0],
  pr_subsubcategory_name: product.categoriesL3?.[0],
  reco_engine_name: engineName,
  search_query: undefined, // not applicable in a playlist context
  playlist_fingerprint: playlistFingerprint,
  playlist_reconciliation: playlistReconciliation,
});

/**
 * Create tracking info for each recommended product during the product bundle view event
 * @param products Bundle products
 * @param listingFingerprint Defaults to null
 * @param playlistLocation Location of the playlist
 * @param playlistFingerprint Gtm fingerprint
 * @param playlistReconciliation Gtm reconciliation
 * @returns ViewBundleProduct
 */
export const viewBundleGtmEvent = (
  products: Product[],
  listingFingerprint: string | null,
  playlistLocation: string,
  recoEngineName: string,
  playlistFingerprint?: string,
  playlistReconciliation?: string,
  position?: number,
): ViewBundleGtmEvent => ({
  event: 'interaction_display playlist',
  e_category: 'ecommerce',
  e_action: 'Display playlist',
  e_label: 'Bundle',
  amp_event_name: 'Display playlist',
  event_trigger: playlistLocation,
  has_promo_product: !!products?.find((product) => product.hasPromoLabel),
  currency: CURRENCY,
  block_clicked: 'Bundle',
  listing_fingerprint: listingFingerprint || '',
  playlist_article_ids: products.map((product) => product.articleId),
  playlist_model_ids: products.map((product) => product.modelId),
  playlist_category_ids: products.map((product) => product.categoriesLastId),
  playlist_product_ids: products.map((product) => product.productId),
  playlist_b2b_product_count: products.filter(
    (product) => product.hasProConditions,
  ).length,
  playlist_content_id: undefined,
  playlist_fingerprint: playlistFingerprint,
  playlist_reconciliation: playlistReconciliation,
  playlist_position: position,
  playlist_product_count: products.length,
  playlist_name: 'Bundle',
  interaction_hit: true,
  mf_product_count: products.filter((product) => product.isMmf).length,
  pro_product_count: products.filter((product) => product.hasProConditions)
    .length,
  pr_products: products.map((product, index) =>
    viewBundleProduct(
      product,
      index,
      recoEngineName,
      playlistFingerprint,
      playlistReconciliation,
    ),
  ),
});

export const clickProductInBundleGtmEvent = (
  allProducts: Product[],
  productPosition: number,
  playlistFingerprint: string,
  playlistReconciliation: string,
  listingFingerprint: string | null,
  recoEngineName: string,
): Partial<ProductCardRecommendationTracking> => {
  const defaultTracking = computeDefaultTrackingData({
    listName: 'Bundle',
    position: productPosition,
    product: allProducts[productPosition],
    price: allProducts[productPosition].prices.main,
  });
  return {
    ...defaultTracking,
    currency: CURRENCY,
    block_clicked: 'Bundle',
    playlist_article_ids: allProducts.map((p) => p.articleId),
    playlist_model_ids: allProducts.map((p) => p.modelId),
    playlist_name: 'Bundle',
    playlist_category_ids: allProducts.map((p) => p.categoriesLastId),
    playlist_product_ids: allProducts.map((p) => p.productId),
    playlist_b2b_product_count: allProducts.filter((p) => p.hasProConditions)
      .length,
    playlist_content_id: undefined,
    playlist_fingerprint: playlistFingerprint,
    playlist_reconciliation: playlistReconciliation,
    listing_fingerprint: listingFingerprint ?? undefined,
    interaction_hit: false,
    pr_products: [
      {
        ...defaultTracking.pr_products[0],
        playlist_fingerprint: playlistFingerprint,
        playlist_reconciliation: playlistReconciliation,
        reco_engine_name: recoEngineName,
      },
    ],
  };
};
